import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query'
import {
  createTransaction,
  updateTransaction,
  deleteTransaction,
  getExpenses,
  getIncomes,
  getSavings,
  getTransfers,
  getAvailableMonths,
  type CreateTransactionPayload,
  type UpdateTransactionPayload,
  type TransactionQueryParams,
} from '../api/transactions'
import { toast } from 'sonner'

export const useCreateTransaction = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: CreateTransactionPayload) => createTransaction(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] })
      queryClient.invalidateQueries({ queryKey: ['dashboardSummary'] })
      queryClient.invalidateQueries({ queryKey: ['accounts'] })
      toast.success('Transacción creada correctamente')
    },
    onError: () => {
      toast.error('Error al crear la transacción')
    },
  })
}

export const useUpdateTransaction = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: UpdateTransactionPayload) => updateTransaction(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] })
      queryClient.invalidateQueries({ queryKey: ['dashboardSummary'] })
      queryClient.invalidateQueries({ queryKey: ['accounts'] })
      toast.success('Transacción actualizada correctamente')
    },
    onError: () => {
      toast.error('Error al actualizar la transacción')
    },
  })
}

export const useDeleteTransaction = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, actionType }: { id: number; actionType: 'INCOME' | 'EXPENSE' | 'SAVING' | 'TRANSFER' }) =>
      deleteTransaction(id, actionType),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] })
      queryClient.invalidateQueries({ queryKey: ['dashboardSummary'] })
      queryClient.invalidateQueries({ queryKey: ['accounts'] })
      toast.success('Transacción eliminada correctamente')
    },
    onError: () => {
      toast.error('Error al eliminar la transacción')
    },
  })
}

export const useAllTransactions = (params?: TransactionQueryParams) => {
  return useQuery({
    queryKey: ['transactions', params?.year ?? 'ALL', params?.month ?? 'ALL', params],
    queryFn: async () => {
      const [expenses, incomes, savings, transfers] = await Promise.all([
        getExpenses(params),
        getIncomes(params),
        getSavings(params),
        getTransfers(params)
      ])
      
      // Combine and add a type flag for UI purposes
      const combined = [
        ...expenses.map(t => ({ ...t, _type: 'EXPENSE' as const })),
        ...incomes.map(t => ({ ...t, _type: 'INCOME' as const })),
        ...savings.map(t => ({ ...t, _type: 'SAVING' as const })),
        ...transfers.map(t => ({
          ...t,
          account: t.from_account,
          _type: 'TRANSFER' as const,
          account_name: t.from_account_name && t.to_account_name ? `${t.from_account_name} ➔ ${t.to_account_name}` : ''
        }))

      ]
      
      // Sort by date descending
      return combined.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    }
  })
}


export const useAvailableMonths = () => {
  return useQuery({
    queryKey: ['transactions', 'available-months'],
    queryFn: getAvailableMonths,
    staleTime: 5 * 60 * 1000,
  })
}


